/**
 * BuddyConnectedAck — the "You've been connected with your BTC Buddy"
 * acknowledgment screen.
 *
 * Shown automatically the first time a member sees an accepted
 * connection. Actions reuse existing endpoints:
 *   - View Buddy      → /portal/matches
 *   - Message Buddy   → /portal/messaging
 *   - Schedule Session → /portal/sessions
 *
 * On "Continue", posts /api/northstar/connected-ack/seen (which cannot
 * touch activation state) and routes to /northstar/welcome if
 * activated but awakening not yet seen — otherwise to /northstar.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Heart, CheckCircle2, Users, MessageCircle, Calendar } from "lucide-react";
import { api } from "../../lib/auth";
import { resolveLandingRoute } from "../../lib/experienceResolver";
import { useActivation } from "../../hooks/useActivation";

export default function BuddyConnectedAck() {
  const { data: activation, loading } = useActivation();
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/northstar/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB]">
        <Heart className="w-8 h-8 text-[#23803F] animate-pulse" />
      </div>
    );
  }
  if (!activation) return <Navigate to="/portal" replace />;

  // Only meaningful if the member is actually connected/activated.
  if (activation.status !== "activated" && activation.base_status !== "buddy_connected") {
    return <Navigate to="/portal" replace />;
  }

  const onContinue = async () => {
    try {
      await api.post("/northstar/connected-ack/seen");
    } catch (_e) {
      // Non-fatal — routing still proceeds.
    }
    // Route through the Experience Resolver — the post-ack
    // destination depends on activation + awakening state, which the
    // resolver already computes.  Fall back to the awakening/portal
    // hierarchy on resolver failure so the user is never stranded.
    try {
      const dest = await resolveLandingRoute();
      navigate(dest, { replace: true });
      return;
    } catch (_e) {
      /* fall-through — resolver unavailable */
    }
    if (activation.status === "activated" && !profile?.awakening_seen_at) {
      navigate("/northstar/welcome", { replace: true });
    } else if (activation.status === "activated") {
      navigate("/northstar", { replace: true });
    } else {
      navigate("/portal", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center p-6" data-testid="buddy-connected-ack">
      <div className="max-w-2xl w-full rounded-3xl bg-white border border-gray-100 p-8 lg:p-12 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-[#2D9B4E] text-white flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#23803F]">
            You&rsquo;re connected
          </p>
        </div>

        <h1
          className="text-3xl sm:text-4xl font-extrabold text-[#0B132B] tracking-tight leading-tight"
          data-testid="ack-headline"
        >
          You&rsquo;ve been connected with your BTC Buddy.
        </h1>
        <p className="text-[#4B5563] mt-4 text-lg leading-relaxed">
          This is the beginning of your journey together. Take your time.
          Your Buddy is here alongside you &mdash; no pressure to rush anything.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <Link
            to="/portal/matches"
            className="rounded-2xl bg-[#F0F7F2] border border-[#DFEDE4] p-5 hover:border-[#2D9B4E] transition-colors group"
            data-testid="ack-action-view-buddy"
          >
            <Users className="w-5 h-5 text-[#23803F] mb-2" />
            <p className="font-semibold text-[#0B132B] text-sm">View Buddy</p>
            <p className="text-xs text-[#4B5563] mt-1">See your matches and connection.</p>
          </Link>
          <Link
            to="/portal/messaging"
            className="rounded-2xl bg-[#F0F4FA] border border-[#DDE6F1] p-5 hover:border-[#1A5FAB] transition-colors group"
            data-testid="ack-action-message"
          >
            <MessageCircle className="w-5 h-5 text-[#1A5FAB] mb-2" />
            <p className="font-semibold text-[#0B132B] text-sm">Message Buddy</p>
            <p className="text-xs text-[#4B5563] mt-1">Say hello when you&rsquo;re ready.</p>
          </Link>
          <Link
            to="/portal/sessions"
            className="rounded-2xl bg-[#F5F0FA] border border-[#E4DAF0] p-5 hover:border-[#7C4DBE] transition-colors group"
            data-testid="ack-action-schedule"
          >
            <Calendar className="w-5 h-5 text-[#7C4DBE] mb-2" />
            <p className="font-semibold text-[#0B132B] text-sm">Schedule Session</p>
            <p className="text-xs text-[#4B5563] mt-1">Book your first video session.</p>
          </Link>
        </div>

        <button
          onClick={onContinue}
          className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0B132B] text-white font-semibold hover:bg-[#1a2a4d] transition-colors"
          data-testid="ack-continue"
        >
          Enter Northstar &rarr;
        </button>
      </div>
    </div>
  );
}
